import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Card,
    CardContent,
    Grid,
    Checkbox,
    Input,
    Radio,
    RadioGroup,
    FormLabel
} from '@mui/material';
import axios from 'axios';
import './Institution.scss';
// Import membership data - make sure the path is correct
import membershipData from '../../data.json';
import PhoneIcon from '@mui/icons-material/Phone';

const Institution = () => {
    // Helper function to check if the category data is valid
    const isValidCategoryData = (data, categoryType) => {
        return data && 
               typeof data === 'object' && 
               data[categoryType] && 
               Array.isArray(data[categoryType]);
    };
    
    // Helper function to safely get membership types
    const getMembershipTypes = (data, categoryType) => {
        if (!isValidCategoryData(data, categoryType)) {
            return [];
        }
        
        try {
            // Extract base membership types by removing "(New Member)" and "(Existing Member)"
            const baseTypes = data[categoryType].map(item => {
              return item.membership_type
                .replace(' (New Member)', '')
                .replace(' (Existing Member)', '');
            });
            return [...new Set(baseTypes)];
          } catch (error) {
            console.error("Error extracting membership types:", error);
            return [];
          }
        };
    
    // Helper function to safely get tenures for a membership type
    const getTenures = (data, categoryType, baseMembershipType) => {
        if (!isValidCategoryData(data, categoryType)) {
          return [];
        }
        
        try {
          // Look for both new and existing member entries for this base type
          const tenures = data[categoryType]
            .filter(item => {
              const baseType = item.membership_type
                .replace(' (New Member)', '')
                .replace(' (Existing Member)', '');
              return baseType === baseMembershipType;
            })
            .map(item => item.tenure);
          return [...new Set(tenures)];
        } catch (error) {
          console.error("Error extracting tenures:", error);
          return [];
        }
      };
      
      // Helper function to safely get fee details
      const getFeeDetails = (data, categoryType, baseMembershipType, tenure, isNewMember) => {
        if (!isValidCategoryData(data, categoryType)) {
          return { membershipFee: 0, entranceFee: 0, totalFee: 0, gst: 0 };
        }
        
        try {
          // Construct the full membership type based on new/existing status
          const fullMembershipType = `${baseMembershipType} (${isNewMember ? 'New Member' : 'Existing Member'})`;
          
          const item = data[categoryType].find(
            item => item.membership_type === fullMembershipType && item.tenure === tenure
          );
          
          if (item) {
            return {
              membershipFee: item.membership_fee || 0,
              entranceFee: item.entrance_fee || 0,
              gst: item.gst || 0,
              totalFee: item.grand_total || 0
            };
          }
        } catch (error) {
          console.error("Error getting fee details:", error);
        }
        
        return { membershipFee: 0, entranceFee: 0, gst: 0, totalFee: 0 };
      };

    const [activeStep, setActiveStep] = useState(0);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
    const [membershipType, setMembershipType] = useState(''); // 'new' or 'old'
    const [fileName, setFileName] = useState('');
    const [feeDetails, setFeeDetails] = useState({
        membershipFee: 0,
        entranceFee: 0,
        gst: 0,
        totalFee: 0
    });
    const [formData, setFormData] = useState({
        nameofprincipalorsecretary: '',
        collegeName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        phoneno: '',
        gstnumber: '',
        panNumber: '',
        linkedin: '',
        principalorviceprincipal: '',
        yearOfEstablishment: '',
        numberOfStudentsandFaculty: '',
        telephoneOffice: '',
        email: '',
        website: '',
        communicationAuthority: {
            name: '',
            designation: '',
            phone: '',
            email: ''
        },
        achievements: '',
        additionalInfo: '',
        howDidYouKnowAboutUs: '',
        membershipCategory: '',
        tenure: 'Yearly', // Default tenure
        membershipID: ''
    });

    // Update fee details when membership category or tenure changes (for both new and existing members)
    useEffect(() => {
        if (formData.membershipCategory && formData.tenure && membershipType) {
            const isNewMember = membershipType === 'new';
            const newFeeDetails = getFeeDetails(
                membershipData, 
                'institution_category', 
                formData.membershipCategory,
                formData.tenure,
                isNewMember
            );
            
            setFeeDetails(newFeeDetails);
        }
    }, [formData.membershipCategory, formData.tenure, membershipType]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('communicationAuthority.')) {
            const key = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                communicationAuthority: {
                    ...prev.communicationAuthority,
                    [key]: value
                }
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleMembershipTypeChange = (e) => {
        setMembershipType(e.target.value);
        // Clear member ID if switching from old to new
        if (e.target.value === 'new') {
            setFormData(prev => ({ ...prev, membershipID: '' }));
        }
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!agreeToTerms || !agreeToPrivacy) {
            alert('Please agree to the terms and privacy policy.');
            return;
        }

        if (!membershipType) {
            alert('Please select whether you are a new or existing member.');
            return;
        }

        if (membershipType === 'old' && !formData.membershipID) {
            alert('Please enter your existing membership ID.');
            return;
        }

        try {
            // Create form data object for file upload
            const formDataToSend = new FormData();
            
            // Add membership type
            formDataToSend.append('membershipType', membershipType);
            
            // Add all form fields to FormData
            Object.keys(formData).forEach(key => {
                if (key === 'communicationAuthority') {
                    // Handle nested object
                    Object.keys(formData.communicationAuthority).forEach(subKey => {
                        formDataToSend.append(
                            `communicationAuthority.${subKey}`, 
                            formData.communicationAuthority[subKey]
                        );
                    });
                } else {
                    formDataToSend.append(key, formData[key]);
                }
            });
            
            const response = await axios.post(
                'https://form.cpccbe.org/api/institution/institution', 
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            alert(response.data.message || 'Membership application submitted successfully!');
            
            // Reset form data after submission
            setFormData({
                nameofprincipalorsecretary: '',
                collegeName: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                postalCode: '',
                phoneno: '',
                gstnumber: '',
                panNumber: '',
                linkedin: '',
                principalorviceprincipal: '',
                yearOfEstablishment: '',
                numberOfStudentsandFaculty: '',
                telephoneOffice: '',
                email: '',
                website: '',
                communicationAuthority: {
                    name: '',
                    designation: '',
                    phone: '',
                    email: ''
                },
                achievements:'',
                additionalInfo:'',
                howDidYouKnowAboutUs:'',
                membershipCategory: '',
                tenure: 'Yearly',
                membershipID: ''
            });
            setFileName('');
            setMembershipType('');
            setAgreeToTerms(false);
            setAgreeToPrivacy(false);
            setFeeDetails({
                membershipFee: 0,
                entranceFee: 0,
                gst: 0,
                totalFee: 0
            });
            setActiveStep(0); // Reset to first step after submission
        } catch (error) {
            console.error("Error submitting form:", error);
            
            // Display specific error message from server if available
            if (error.response && error.response.data && error.response.data.error) {
                alert(error.response.data.error);
            } else if (error.response && error.response.status === 409) {
                alert('This email and college name already exists in our system. Please check your details or contact support.');
            } else {
                alert('Error submitting the form. Please try again.');
            }
        }
    };

    return (
        <Card sx={{ maxWidth:'600px', margin:'auto', padding:'20px', marginTop:'20px' }}>
            <CardContent>
                <Typography variant="h4" component="h2" gutterBottom>
                    Institution - Membership Application Form
                </Typography>

                <Stepper activeStep={activeStep} alternativeLabel>
                    {Array.from({ length : 5 }, (_, index) => (
                        <Step key={index}>
                            <StepLabel>{`Step ${index + 1}`}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <form onSubmit={handleSubmit}>
                    {activeStep === 0 && (
                        <>
                            <Box sx={{ mt : 2 }}>
                                <TextField fullWidth margin="normal" label="Name of Principal / Secretary" name="nameofprincipalorsecretary" value={formData.nameofprincipalorsecretary} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="College Name" name="collegeName" value={formData.collegeName} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Address Line 1" name="address1" value={formData.address1} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Address Line 2" name="address2" value={formData.address2} onChange={handleChange} />
                                <TextField fullWidth margin="normal" label="City" name="city" value={formData.city} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="State" name="state" value={formData.state} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Postal Code" name="postalCode" value={formData.postalCode} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Phone Number" name="phoneno" value={formData.phoneno} onChange={handleChange} required />
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>
                                <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 1 && (
                        <>
                            <Box sx={{ mt : 2 }}>
                                <TextField fullWidth margin="normal" label="GST Number (Optional)" name="gstnumber" value={formData.gstnumber} onChange={handleChange} />
                                <TextField fullWidth margin="normal" label="PAN Number (Optional)" name="panNumber" value={formData.panNumber} onChange={handleChange}/>
                                <TextField fullWidth margin="normal" label="LinkedIn ID" name="linkedin" value={formData.linkedin} onChange={handleChange} required/>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Year of Establishment"
                                    name="yearOfEstablishment"
                                    value={formData.yearOfEstablishment}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="YYYY"
                                    inputProps={{ maxLength: 4 }}
                                    required
                                />
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="students-faculty-count-label">Number of Students and Faculty</InputLabel>
                                    <Select
                                        labelId="students-faculty-count-label"
                                        id="students-faculty-count"
                                        name="numberOfStudentsandFaculty"
                                        value={formData.numberOfStudentsandFaculty}
                                        onChange={handleChange}
                                        label="Number of Students and Faculty"
                                        required
                                    >
                                        <MenuItem value="" disabled>Select Number of Students and Faculty</MenuItem>
                                        <MenuItem value="0-50">0-50</MenuItem>
                                        <MenuItem value="51-100">51-100</MenuItem>
                                        <MenuItem value="101-500">101-500</MenuItem>
                                        <MenuItem value="501-1000">501-1000</MenuItem>
                                        <MenuItem value="1001-5000">1001-5000</MenuItem>
                                        <MenuItem value="5001-10000">5001-10000</MenuItem>
                                        <MenuItem value="10001-50000">10001-50000</MenuItem>
                                        <MenuItem value="50001-100000">50001-100000</MenuItem>
                                        <MenuItem value="100001 and above">100001 and above</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField fullWidth margin="normal" label="Telephone (Office)" name="telephoneOffice" value={formData.telephoneOffice} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Email" name="email" value={formData.email} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Website" name="website" value={formData.website} onChange={handleChange} required />
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 2 && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <TextField fullWidth margin="normal" label="Communication Authority Name" name="communicationAuthority.name" value={formData.communicationAuthority.name} onChange={handleChange} required />   
                                <TextField fullWidth margin="normal" label="Communication Authority Designation" name="communicationAuthority.designation" value={formData.communicationAuthority.designation} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Communication Authority Whatsapp Number" name="communicationAuthority.phone" value={formData.communicationAuthority.phone} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Communication Authority Email" name="communicationAuthority.email" value={formData.communicationAuthority.email} onChange={handleChange} required />
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 3 && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <TextField fullWidth margin="normal" label="Achievements" name="achievements" value={formData.achievements} onChange={handleChange}/>
                                <TextField fullWidth margin="normal" label="Additional Information" name="additionalInfo" value={formData.additionalInfo} onChange={handleChange}/>
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel id="howDidYouKnowAboutUs-label">How did you know about us?</InputLabel>
                                    <Select 
                                        labelId="howDidYouKnowAboutUs-label"
                                        id="howDidYouKnowAboutUs"
                                        name="howDidYouKnowAboutUs" 
                                        value={formData.howDidYouKnowAboutUs} 
                                        onChange={handleChange}
                                        label="How did you know about us?"
                                    >
                                        <MenuItem value="" disabled>Select option</MenuItem>
                                        <MenuItem value="Google">Google</MenuItem>
                                        <MenuItem value="Facebook">Facebook</MenuItem>
                                        <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                                        <MenuItem value="Twitter">Twitter</MenuItem>
                                        <MenuItem value="Instagram">Instagram</MenuItem>
                                        <MenuItem value="Referral">Referral</MenuItem>
                                        <MenuItem value="Others">Others</MenuItem>  
                                    </Select>
                                </FormControl>

                                {/* Membership Type Selection - After "How did you know about us" */}
                                <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }} required>
                                    <FormLabel component="legend">Are you a new or existing member? *</FormLabel>
                                    <RadioGroup
                                        row
                                        value={membershipType}
                                        onChange={handleMembershipTypeChange}
                                        name="membershipType"
                                    >
                                        <FormControlLabel value="new" control={<Radio />} label="New Member" />
                                        <FormControlLabel value="old" control={<Radio />} label="Existing Member" />
                                    </RadioGroup>
                                </FormControl>

                                {/* Show Member ID field only for existing members */}
                                {membershipType === 'old' && (
                                    <TextField 
                                        fullWidth 
                                        margin="normal" 
                                        label="Existing Membership ID" 
                                        name="membershipID" 
                                        value={formData.membershipID} 
                                        onChange={handleChange} 
                                        required 
                                        helperText="Please enter your existing membership ID"
                                        sx={{ mb: 2 }}
                                    />
                                )}
                                
                                {/* Membership Category and Tenure Selection */}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="membershipCategory-label">Membership Category</InputLabel>
                                            <Select
                                                labelId="membershipCategory-label"
                                                id="membershipCategory"
                                                name="membershipCategory"
                                                value={formData.membershipCategory}
                                                onChange={handleChange}
                                                label="Membership Category"
                                                required
                                            >
                                                <MenuItem value="" disabled>Select Membership Category</MenuItem>
                                                {getMembershipTypes(membershipData, 'institution_category').map(type => (
                                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="tenure-label">Tenure</InputLabel>
                                            <Select
                                                labelId="tenure-label"
                                                id="tenure"
                                                name="tenure"
                                                value={formData.tenure}
                                                onChange={handleChange}
                                                label="Tenure"
                                                required
                                            >
                                                <MenuItem value="" disabled>Select Tenure</MenuItem>
                                                {getTenures(membershipData, 'institution_category', formData.membershipCategory).map(tenure => (
                                                    <MenuItem key={tenure} value={tenure}>{tenure}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                
                                {/* Fee Details Display - For both new and existing members */}
                                {formData.membershipCategory && membershipType && (
                                    <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#f5f5f5' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Fee Details - {membershipType === 'new' ? 'New Member' : 'Existing Member'}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Membership Fee (₹)"
                                                    value={feeDetails.membershipFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Entrance Fee (₹)"
                                                    value={membershipType === 'old' ? '0 (Waived for existing members)' : feeDetails.entranceFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="GST (₹)"
                                                    value={feeDetails.gst}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Total Fee (₹)"
                                                    value={feeDetails.totalFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </Grid>
                                        </Grid>
                                        {membershipType === 'old' && (
                                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#1976d2' }}>
                                                * Entrance fee is waived for existing members
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button 
                                    variant='contained' 
                                    color='primary' 
                                    onClick={handleNext}
                                    disabled={!membershipType}
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 4 && (
                        <>
                            <br />
                            <hr />
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Details
                                </Typography>
                                
                                {/* Display selected membership details */}
                                {formData.membershipCategory && (
                                    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#f9f9f9' }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Selected Membership Details:
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Member Type:</strong> {membershipType === 'new' ? 'New Member' : 'Existing Member'}
                                        </Typography>
                                        {membershipType === 'old' && (
                                            <Typography variant="body1">
                                                <strong>Existing Member ID:</strong> {formData.membershipID}
                                            </Typography>
                                        )}
                                        <Typography variant="body1">
                                            <strong>Category:</strong> {formData.membershipCategory}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Tenure:</strong> {formData.tenure}
                                        </Typography>
                                        
                                        <Typography variant="body1">
                                            <strong>Membership Fee:</strong> ₹{feeDetails.membershipFee}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Entrance Fee:</strong> {membershipType === 'old' ? '₹0 (Waived)' : `₹${feeDetails.entranceFee}`}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>GST:</strong> ₹{feeDetails.gst}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1, fontSize: '1.1rem', color: '#1976d2' }}>
                                            <strong>Total Amount Payable:</strong> ₹{feeDetails.totalFee}
                                        </Typography>
                                    </Box>
                                )}
                                

                                {/* Thank you message */}
                                <Box sx={{ mt: 3, p: 3, border: '2px solid #1976d2', borderRadius: '8px', bgcolor: '#f8f9fa', textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
                                        Thank you for your application for membership in THE COIMBATORE PRODUCTIVITY COUNCIL.
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We appreciate your interest in joining our community.
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We will review your application and get in touch shortly regarding the next steps.
                                    </Typography>
                                     <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        In the meantime, if you have any questions, please don't hesitate to contact us at +91 822 00 00 371 / 2 / 3 / 4 or cpc@cpccbe.org
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We appreciate your patience and look forward to connecting with you.
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                        TEAM – ADMIN<br />
                                        THE COIMBATORE PRODUCTIVITY COUNCIL
                                    </Typography>
                                </Box>

                            </Box>
                            <hr />
                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={<Checkbox checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} />}
                                    label="I agree to the terms and conditions of CPCCBE."
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={agreeToPrivacy} onChange={(e) => setAgreeToPrivacy(e.target.checked)} />}
                                    label="I consent to the collection and use of my data as per the Privacy Policy."
                                />
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>  
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button variant='contained' color='primary' type='submit'>Submit</Button>
                            </Box>
                        </>
                    )}
                </form>
            </CardContent>
        </Card>
    );
};

export default Institution;